import { DatePicker, LeaderGroupCombobox, ModuleWrapper } from '@/components'
import { Card } from '@/components_deprecated/Card'
import { SelectMultipleUsers } from '@/components_deprecated/Select/SelectMultipleUsers'
import { DropdownNames } from '@/components_deprecated/Select/types'
import { PageType } from '@/modules/common/consts'
import { Module } from '@/modules/companies'
import {
  useAdmin,
  useLeaderGroups,
  usePermissions,
  useUser,
} from '@/utils/hooks'
import { useEffect, useMemo, useRef } from 'react'
import { PERMISSIONS } from '@/utils/permissions'
import type { JSONValue } from '@ur-solutions/react-hooks'
import { useForm, useTranslate } from '@ur-solutions/react-hooks'
import { endOfMonth, isValid, startOfMonth } from 'date-fns'
import { isDesktop } from 'react-device-detect'
import styled from 'styled-components'
import { InfringementPopupFilter } from '../components'
import { InfringementsTable } from '../components/table/InfringementsTable'
import type { InfringementsTableFilter, SeverityCategory } from '../types'
import type { InfringementCategory } from '../types.graphl'

const FilterSection = styled.section`
  grid-area: filters;

  display: flex;
  gap: 0.75rem;

  & > * {
    max-width: 100%;
  }
`

const SelectUsersWrapper = styled.div`
  overflow: visible;
  height: 54px;
`

const AreaInfringementPopupFilter = styled.div`
  display: flex;
  width: 250px;

  & > div {
    width: 100%;
  }
`

const MonthFlexArea = styled.div`
  display: flex;
  width: 320px;
  gap: 1rem;
`

const Controls = styled(Card)`
  width: 100%;
  box-sizing: border-box;
  overflow-x: wrap;
  gap: 1rem;
  margin-bottom: 1rem;
  display: flex;
`

const DEFAULT_DATE_RANGE = {
  from: startOfMonth(new Date()),
  to: endOfMonth(new Date()),
}

export const Infringements: React.FC = () => {
  const translations = useTranslate({
    infringements: 'common.infringements',
    users: 'common.users',
    user: 'common.user',
    export: 'common.export',
    archived: 'common.archived',
    chooseGroup: 'common.choose-group',

    success: 'toasts.email-sent-successfully',
    error: 'errors.generic-error',
  })

  const admin = useAdmin()
  const me = useUser()
  const { hasPermissions } = usePermissions()

  const { hasAccess, myLeaderGroups, groupsLoading } = useLeaderGroups(
    Module.INFRINGEMENTS
  )

  const canViewOthers = hasPermissions(
    PERMISSIONS.infringements.view.othersinfringement,
    true
  )

  const {
    formValues: filter,
    formChangeHandler: filterHandler,
    updateForm,
  } = useForm<InfringementsTableFilter>({
    values: {
      query: '',
      orderBy: '-infringementTimestamp',
      userOrderBy: '-firstName',
      excludeSeverities: [],
      excludeCategories: [],
      archived: false,
      users: [],
      dateRange: DEFAULT_DATE_RANGE,
      leaderGroup: null,
    },
    config: {
      storage: {
        storeFormState: true,
        retrieveFormStateOnMount: true,
        sessionStorage: true,
        storeFormStateName: 'firmadok__infringements-overview-filter',
        transformValues: {
          orderBy: value => (typeof value === 'string' ? value : undefined),
          userOrderBy: value => (typeof value === 'string' ? value : undefined),
          users: value => {
            if (Array.isArray(value)) {
              return value.filter(val => typeof val === 'string') as string[]
            }
            return []
          },
          dateRange: value => {
            if (
              !value ||
              typeof value !== 'object' ||
              Array.isArray(value) ||
              !('from' in value) ||
              !('to' in value)
            ) {
              return DEFAULT_DATE_RANGE
            }

            const from = new Date(value.from as string)
            const to = new Date(value.to as string)

            if (!isValid(from) || !isValid(to)) return DEFAULT_DATE_RANGE

            return { from, to }
          },
          leaderGroup: value => (typeof value === 'string' ? value : null),
          excludeSeverities: value => {
            function isSeverityCategory(
              value: JSONValue
            ): value is SeverityCategory {
              return (
                typeof value === 'string' &&
                ['MINOR', 'SERIOUS', 'VERY_SERIOUS', 'MOST_SERIOUS'].includes(
                  value
                )
              )
            }
            if (Array.isArray(value)) {
              return value.filter(val =>
                isSeverityCategory(val)
              ) as SeverityCategory[]
            }
            return []
          },
          excludeCategories: value => {
            if (Array.isArray(value)) {
              return value as InfringementCategory[]
            }
            return []
          },
        },
      },
    },
  })
  const didInitializeFilter = useRef(false)

  const selectedLeaderGroupUsers = useMemo(
    () =>
      new Set(
        myLeaderGroups
          .find(group => group.id === filter.leaderGroup)
          ?.users.map(user => user.id) ?? []
      ),
    [filter.leaderGroup, myLeaderGroups]
  )

  // When the leader group changes, we need to check if the currently selected users are still valid.
  const handleLeaderGroupChange = (value: string) => {
    const leaderGroup = value || null

    if (!leaderGroup) {
      updateForm({ leaderGroup })
      return
    }

    const selectedGroup = myLeaderGroups.find(group => group.id === leaderGroup)
    const selectedGroupUserIds = new Set(
      selectedGroup?.users.map(user => user.id) ?? []
    )

    const users = filter.users.filter(userId =>
      selectedGroupUserIds.has(userId)
    )

    const usersChanged =
      users.length !== filter.users.length ||
      users.some((userId, index) => userId !== filter.users[index])

    if (!usersChanged) {
      updateForm({ leaderGroup })
      return
    }

    updateForm({ leaderGroup, users })
  }

  // Initialize filter on mount based on permissions and groups.
  useEffect(() => {
    if (didInitializeFilter.current) return
    if (groupsLoading) return

    // Users without permission to view others are always scoped to themselves.
    if (!admin && !canViewOthers) {
      updateForm({
        users: [me.id],
        leaderGroup: null,
      })
      didInitializeFilter.current = true
      return
    }

    // Default state is empty selectors.
    const nextFilter: Partial<InfringementsTableFilter> = {
      users: [],
      leaderGroup: null,
    }

    // Leaders with exactly one group get that group pre-selected.
    if (hasAccess && myLeaderGroups.length === 1 && myLeaderGroups[0]) {
      nextFilter.leaderGroup = myLeaderGroups[0].id
    }

    updateForm(nextFilter)
    didInitializeFilter.current = true
  }, [
    admin,
    canViewOthers,
    groupsLoading,
    hasAccess,
    me.id,
    myLeaderGroups,
    updateForm,
  ])

  return (
    <ModuleWrapper
      title={
        isDesktop
          ? `${translations.infringements}${
              filter.archived ? ' (' + translations.archived + ')' : ''
            }`
          : translations.infringements
      }
      page={PageType.INFRINGEMENTS}
    >
      <Controls>
        <FilterSection className="flex-wrap">
          {isDesktop && canViewOthers && (
            <SelectUsersWrapper>
              <SelectMultipleUsers
                minWidth="260px"
                maxWidth="100%"
                selected={filter.users}
                dropdownPageName={DropdownNames.ACTIVITIES}
                placeholder={translations.user}
                onChange={filterHandler('users')}
                hideItem={item =>
                  !!filter.leaderGroup && !selectedLeaderGroupUsers.has(item.id)
                }
              />
            </SelectUsersWrapper>
          )}

          {hasAccess && canViewOthers && (
            <div>
              <LeaderGroupCombobox
                mode="single"
                module={Module.INFRINGEMENTS}
                value={filter.leaderGroup ?? ''}
                clearable
                placeholder={translations.chooseGroup}
                onChange={val => {
                  handleLeaderGroupChange(val)
                }}
              />
            </div>
          )}

          <MonthFlexArea>
            <DatePicker
              mode="range"
              value={filter.dateRange}
              format="PP"
              onChange={value => {
                if (!value?.from || !value.to) return
                filterHandler('dateRange')({ from: value.from, to: value.to })
              }}
            />
          </MonthFlexArea>

          <AreaInfringementPopupFilter>
            <InfringementPopupFilter filter={filter} onUpdate={updateForm} />
          </AreaInfringementPopupFilter>
        </FilterSection>
      </Controls>

      <InfringementsTable filter={filter} onSort={filterHandler('orderBy')} />
    </ModuleWrapper>
  )
}
